// Section 12: the settlement-level levers on the per-person illness roll, and the settlement
// illness multiplier the Health tab explains.
//   Contagion: 1 + 0.4 x (weighted sick / population); plague counts 2x ("very, 2x contagion"),
//     non-contagious illness 0x (D-18). People isolated by a quarantine (Section 19) do not count.
//     The clinic's 0.7x is the clinic's own modifier.
//   Sick water carriers foul the water: 1.5x while any carrier (not isolated) is sick.
//   Sick arrivals (Section 14): 1.5x until quarantine.contagionBoostUntilTurn.
//   Uncovered under-6s (Sections 8 and 15): 1.5x (core/queries uncoveredUnderSix).
// `illnessMultiplier` is the mean, over everyone not already sick, of illnessRisk / base chance.
// Its rows attribute that mean to each lever in contributor order (sequential attribution: row k
// is the mean of the first k factors over the mean of the first k-1), so their product is exactly
// the mean and a settlement-wide lever shows its own factor. Constants: config.illness,
// config.illnesses.*.contagion, config.migration.sickArrivalContagion.
import { defineContributor } from '../../core/registry';
import { collect, evaluate, mod } from '../../core/modifiers';
import { memoByState } from '../../core/memo';
import { isIsolated, peopleSorted, uncoveredUnderSix } from '../../core/queries';
import type { Modifier, PersonId, ReadContext } from '../../core/types';

interface Spread {
  /** Contagion-weighted count of sick people who are not isolated. */
  weighted: number;
  population: number;
  isolated: number;
  sickCarriers: number;
}

function spread(ctx: ReadContext): Spread {
  return memoByState(ctx.state, 'contagion', () => {
    const out: Spread = { weighted: 0, population: 0, isolated: 0, sickCarriers: 0 };
    for (const p of peopleSorted(ctx.state)) {
      out.population++;
      const sick = p.status.sick;
      if (sick === null) continue;
      if (isIsolated(ctx.cfg, ctx.state, p, ctx.season.turn)) {
        out.isolated++;
        continue;
      }
      out.weighted += ctx.cfg.illnesses[sick.illnessId].contagion;
      if (p.job === 'water-carrier') out.sickCarriers++;
    }
    return out;
  });
}

function uncovered(ctx: ReadContext): ReadonlySet<PersonId> {
  return memoByState(
    ctx.state,
    'contagion:uncovered',
    () => new Set(uncoveredUnderSix(ctx.cfg, ctx.state)),
  );
}

function riskModifiers(ctx: ReadContext, personId: PersonId): Modifier[] {
  const cfg = ctx.cfg;
  const s = spread(ctx);
  const out: Modifier[] = [];
  if (s.weighted > 0 && s.population > 0) {
    const share = s.weighted / s.population;
    const isolated = s.isolated > 0 ? `, ${s.isolated} isolated` : '';
    out.push(
      mod.mul(
        'illness:contagion',
        1 + cfg.illness.contagionPerSickShare * share,
        `${Math.round(100 * share)}% sick${isolated}`,
      ),
    );
  }
  if (s.sickCarriers > 0) {
    out.push(
      mod.mul(
        'water:sick-carrier',
        cfg.illness.sickCarrierMul,
        `${s.sickCarriers} sick water carrier${s.sickCarriers === 1 ? '' : 's'}`,
      ),
    );
  }
  const boost = ctx.state.quarantine.contagionBoostUntilTurn;
  if (boost !== null && ctx.season.turn <= boost) {
    out.push(mod.mul('migration:sick-arrival', cfg.migration.sickArrivalContagion, 'sick arrival'));
  }
  if (uncovered(ctx).has(personId)) {
    out.push(mod.mul('care:uncovered', cfg.illness.underSixMul, 'no one minding them'));
  }
  return out;
}

/** Rows keyed by source and note shape, so "no shelter" and "2 over bed capacity" stay apart. */
function rowKey(source: string, note: string | undefined): string {
  return `${source}|${(note ?? '').replace(/\d+(\.\d+)?/g, '#')}`;
}

function multiplierRows(ctx: ReadContext): readonly Modifier[] {
  return memoByState(ctx.state, 'contagion:multiplier', () => {
    const base = ctx.cfg.illness.baseChance;
    const people = peopleSorted(ctx.state).filter((p) => p.status.sick === null);
    if (people.length === 0 || base <= 0) return [];
    const order: string[] = [];
    const labels = new Map<string, { source: string; note: string | undefined }>();
    // Notes repeat from person to person; key each distinct one once (Section 2 budget).
    const keys = new Map<string, string>();
    const keyOf = (source: string, note: string | undefined): string => {
      const raw = `${source}|${note ?? ''}`;
      let key = keys.get(raw);
      if (key === undefined) {
        key = rowKey(source, note);
        keys.set(raw, key);
      }
      return key;
    };
    const perPerson = people.map((p) => {
      const evaluation = evaluate(base, collect(ctx, { kind: 'illnessRisk', personId: p.id }));
      const ratios = new Map<string, number>();
      let running = base;
      for (const f of evaluation.factors) {
        if (f.op === 'base') continue;
        const next = running + f.delta;
        const ratio = running > 0 ? next / running : 1;
        running = next;
        const key = keyOf(f.source, f.note);
        if (!labels.has(key)) {
          order.push(key);
          labels.set(key, { source: f.source, note: f.note });
        }
        ratios.set(key, (ratios.get(key) ?? 1) * ratio);
      }
      return ratios;
    });
    const products = people.map(() => 1);
    let previous = 1;
    const rows: Modifier[] = [];
    for (const key of order) {
      let sum = 0;
      let affected = 0;
      perPerson.forEach((ratios, i) => {
        const r = ratios.get(key);
        let v = products[i] ?? 1;
        if (r !== undefined) {
          v *= r;
          products[i] = v;
          affected++;
        }
        sum += v;
      });
      const mean = sum / people.length;
      const factor = previous > 0 ? mean / previous : 1;
      previous = mean;
      const label = labels.get(key);
      const who = affected === people.length ? '' : ` (${affected} of ${people.length})`;
      const note = `${label?.note ?? ''}${who}`.trim();
      rows.push(mod.mul(label?.source ?? key, factor, note === '' ? undefined : note));
    }
    return rows;
  });
}

export const contagionContributor = defineContributor({
  slot: 'illnesses',
  id: 'illnesses:contagion',
  targets: ['illnessRisk', 'illnessMultiplier'],
  modifiers(ctx, target) {
    if (target.kind === 'illnessMultiplier') return multiplierRows(ctx);
    if (target.kind !== 'illnessRisk') return [];
    return riskModifiers(ctx, target.personId);
  },
});
